import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * An email field. With a company domain set (BUSINESS_EMAIL_DOMAIN on the server) the person types only the part before the
 * @ and the domain is shown beside it; the value handed up is the full address (empty until a name is typed).
 * Without a domain it is a normal email input. An address already on another domain is shown whole, so it is never mangled.
 */
export function BusinessEmailInput({
  value,
  onChange,
  domain,
  required,
  className,
}: {
  value: string;
  onChange: (email: string) => void;
  domain: string | null;
  required?: boolean;
  className?: string;
}) {
  const legacy = !!domain && value.includes("@") && !value.toLowerCase().endsWith(`@${domain}`);
  if (!domain || legacy) {
    return <Input required={required} type="email" value={value} onChange={(e) => onChange(e.target.value)} className={className} />;
  }
  const local = value.split("@")[0] || "";
  return (
    <div className={cn("flex h-9 w-full items-center rounded-md border border-input bg-background text-sm focus-within:ring-1 focus-within:ring-ring", className)}>
      <input
        required={required}
        value={local}
        autoComplete="off"
        spellCheck={false}
        placeholder="name"
        aria-label={`Email address before @${domain}`}
        // Whatever follows a pasted "@" is dropped, so a full address pasted in keeps just its name part
        onChange={(e) => {
          const name = e.target.value.split("@")[0]!.toLowerCase().replace(/[^a-z0-9._+-]/g, "");
          onChange(name ? `${name}@${domain}` : "");
        }}
        className="h-full min-w-0 flex-1 bg-transparent px-3 outline-none"
      />
      <span className="select-none whitespace-nowrap border-l bg-muted/50 px-3 py-2 text-muted-foreground">@{domain}</span>
    </div>
  );
}
